import styles from './PlaybackScrubber.module.scss';
import {
  InvestigationEnitityIdProps,
  setCurrentTime,
  setIsPlaying,
  useInvestigationEntity,
} from '@customer/store/slices/investigationsSlice';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch } from '@customer/store/hooks';
import Slider from '@cas/components/inputs/slider/Slider';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';

interface UseInterruptPlaybackProps extends InvestigationEnitityIdProps {
  isPlaying: boolean;
}

const useInterruptPlayback = ({
  siteId,
  investigationId,
  isPlaying,
}: UseInterruptPlaybackProps): RefObject<HTMLVdsBasicSliderContinuousElement> => {
  const dispatch = useAppDispatch();

  const sliderRef = useRef<HTMLVdsBasicSliderContinuousElement>(null);

  // Callback to interrupt playback when the user clicks on the slider.
  const stopPlayback = useCallback(() => {
    if (isPlaying) {
      dispatch(setIsPlaying({ siteId, investigationId, isPlaying: false }));
    }
  }, [dispatch, siteId, investigationId, isPlaying]);

  // This effect adds a one-time event listener to the slider to stop playback when the user clicks on the slider.
  // The event listener is removed and then added every time the component is rendered or props change.
  useEffect(() => {
    const currentRef = sliderRef.current;
    const callback = stopPlayback;

    currentRef?.addEventListener('mousedown', callback, { once: true });

    return () => {
      currentRef?.removeEventListener('mousedown', callback);
    };
  }, [stopPlayback]);

  return sliderRef;
};

export const PlaybackScrubber = ({ siteId, investigationId }: InvestigationEnitityIdProps) => {
  const dispatch = useAppDispatch();

  const { showTimeOnlyFromMillis: fromMillis } = useLocaleFormatter();

  const { playback, firstRawTrack, lastRawTrack } = useInvestigationEntity({
    siteId,
    investigationId,
  });

  const minValue = firstRawTrack?.time;
  const maxValue = lastRawTrack?.time;
  const currentTime = playback?.currentTime;
  const isPlaying = playback?.isPlaying ?? false;

  const setNewTime = useCallback(
    (value: number) => {
      dispatch(setCurrentTime({ siteId, investigationId, currentTime: value }));
    },
    [dispatch, siteId, investigationId],
  );

  const sliderRef = useInterruptPlayback({
    siteId,
    investigationId,
    isPlaying,
  });

  const displayMinValue = useMemo<string>(
    () => (typeof minValue === 'number' ? fromMillis(minValue) : ''),
    [fromMillis, minValue],
  );
  const displayMaxValue = useMemo<string>(
    () => (typeof maxValue === 'number' ? fromMillis(maxValue) : ''),
    [fromMillis, maxValue],
  );

  return (
    <div className={styles.scrubber}>
      <div className={styles.slider}>
        <Slider
          elementRef={sliderRef}
          minValue={minValue}
          maxValue={maxValue}
          value={currentTime}
          onChangeCallback={setNewTime}
          debounceDelay={5}
        />
      </div>
      <div className={styles.labels}>
        <div className={styles.labels__label}>{displayMinValue}</div>
        <div className={styles.labels__label}>{displayMaxValue}</div>
      </div>
    </div>
  );
};

export default PlaybackScrubber;
